import React, { useState, useCallback, useEffect } from 'react';
import { MapPinIcon, BoltIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { Link, useRouter } from './../util/router';
import { useAuth } from './../util/auth';
import { getShowSeenByUsers } from '../util/db';
import SeenByAvatars from './SeenByAvatars';
import SeenByModal from './SeenByModal';
import ShowImagePlaceholder from './ShowImagePlaceholder';
import { logShowDetailsClickEvent } from '../util/events';
import { useImpressionTracking, useShowActions } from '../hooks';
import ShowActionButtons from './ShowActionButtons';
import {
  formatDate,
  formatTimeRange,
  formatDateToYYYYMMDD,
  isPastShow,
} from '../util/data-factory';

/** Feed-only cache for seen-by user lists per show; cleared when the user toggles seen on that show. */
const seenByUsersCache = new Map();

function FeedSeenByBlock({ showId }) {
  const auth = useAuth();
  const router = useRouter();
  const [seenByUsers, setSeenByUsers] = useState(() => seenByUsersCache.get(showId) ?? []);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (seenByUsersCache.has(showId)) {
      const cached = seenByUsersCache.get(showId);
      setSeenByUsers(prev => (prev.length === 0 && cached.length > 0 ? cached : prev));
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const users = await getShowSeenByUsers(showId);
        if (!cancelled) {
          seenByUsersCache.set(showId, users);
          setSeenByUsers(users);
        }
      } catch (error) {
        console.error(error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showId]);

  const handleSeenByPress = useCallback(() => {
    if (seenByUsers.length === 0) {
      return;
    }
    if (!auth.user) {
      router.push('/auth/signup');
      return;
    }
    setModalOpen(true);
  }, [auth.user, seenByUsers.length, router]);

  const pressableAriaLabel = auth.user
    ? 'View everyone who has seen this show'
    : 'Sign up to see who has seen this show';

  return (
    <>
      <SeenByAvatars
        seenByUsers={seenByUsers}
        onPress={handleSeenByPress}
        pressableAriaLabel={pressableAriaLabel}
      />
      <SeenByModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        seenByUsers={seenByUsers}
      />
    </>
  );
}

const Content = ({ shows, onSeenToggle, compact, source = 'feed', renderExtra }) => {
  const auth = useAuth();
  const [itemsToShow, setItemsToShow] = useState(50);
  const registerImpressionRef = useImpressionTracking(source);

  const handleSeenToggle = useCallback(
    (showId, result) => {
      seenByUsersCache.delete(showId);
      onSeenToggle?.(showId, result);
    },
    [onSeenToggle]
  );
  const { isSeen, isSaved, handleSeen, handleSave, modals } = useShowActions({
    onSeenToggle: handleSeenToggle,
  });

  useEffect(() => {
    const handleScroll = () => {
      if (itemsToShow >= shows.length) return;
      const threshold = 100;
      if (
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - threshold
      ) {
        setItemsToShow(prevItems => Math.min(prevItems + 50, shows.length));
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [itemsToShow, shows.length]);

  const today = formatDateToYYYYMMDD(new Date());

  const handleShowClick = useCallback(
    showId => {
      void logShowDetailsClickEvent(auth.user?.id ?? null, String(showId), source).catch(err =>
        console.error('Event tracking failed:', err)
      );
    },
    [auth.user?.id, source]
  );

  return (
    <div className="w-full mt-0 overflow-hidden">
      <div
        className={
          compact
            ? 'flex w-full justify-center pb-4 pt-0 px-4 md:pb-8'
            : 'flex w-full justify-center pb-8 pt-0 px-4 md:pb-8'
        }
      >
        <div
          className={
            compact
              ? 'grid grid-cols-1 md:grid-cols-2 gap-6'
              : 'grid grid-cols-1 md:grid-cols-2 gap-10'
          }
        >
          {shows.slice(0, itemsToShow).map(show => (
            <div
              key={show.show_id}
              ref={registerImpressionRef(show.show_id)}
              className="relative overflow-auto border-b border-slate-200 bg-white p-4 font-medium text-black"
            >
              {isPastShow(show.end_date) && (
                <div className="absolute top-5 right-5 z-10">
                  <div className="badge badge-sm normal-case">Past Show</div>
                </div>
              )}
              <a
                href={`/show/${show.show_id}`}
                className="relative md:max-h-[390px] mb-1 md:mb-5 overflow-hidden content-end cursor-pointer block"
                onClick={() => handleShowClick(show.show_id)}
              >
                {show?.image_urls?.[0] ? (
                  <>
                    {show.recommended && (
                      <div className="absolute top-2 left-2 py-0 px-2 bg-off-white flex z-10">
                        <BoltIcon className="w-5" />
                        <small>Trending</small>
                      </div>
                    )}
                    <img className="min-w-full m-auto" src={show.image_urls[0]} alt="" />
                  </>
                ) : (
                  <ShowImagePlaceholder />
                )}
              </a>
              <a
                href={`/show/${show.show_id}`}
                className="text-2xl md:text-4xl my-1 cursor-pointer block"
                onClick={() => handleShowClick(show.show_id)}
              >
                {show.display_title}
              </a>
              <FeedSeenByBlock
                key={`${show.show_id}-${isSeen(show.show_id)}`}
                showId={show.show_id}
              />

              <div className="flex justify-between text-xs md:text-sm my-4">
                <div className="flex flex-1 flex-wrap">
                  <div className="flex mr-4 mb-2 items-center">
                    <MapPinIcon className="w-5" />{' '}
                    {show.venues?.org_id ? (
                      <Link to={`/org/${show.venues.org_id}`} className="hover:underline">
                        {show.venues.venue_name}
                      </Link>
                    ) : (
                      show.venues?.venue_name
                    )}
                  </div>
                  <div className="flex mr-4 mb-2 items-center">
                    <CalendarIcon className="w-5 mr-1" /> {formatDate(show.start_date, true)} -{' '}
                    {formatDate(show.end_date, true)}
                  </div>
                </div>

                <ShowActionButtons
                  showId={show.show_id}
                  showTitle={show.display_title}
                  isSeen={isSeen(show.show_id)}
                  isSaved={isSaved(show.show_id)}
                  onSeenClick={() => void handleSeen(show.show_id)}
                  onSaveClick={() => handleSave(show.show_id)}
                />
              </div>
              {show.opening_date && show.opening_date >= today && (
                <div className="flex normal-case">
                  <p className="text-sm mr-2 uppercase">Opening Reception: </p>
                  <p className="text-sm mr-2 text-black/50">
                    {formatDate(show.opening_date, true)}
                  </p>
                  <p className="text-sm text-black/50">
                    {formatTimeRange(show.opening_start_time, show.opening_end_time)}
                  </p>
                </div>
              )}
              {renderExtra?.(show)}
            </div>
          ))}
        </div>
      </div>
      {modals}
    </div>
  );
};

export default Content;
